import { memo, useEffect } from "react";
import { FaHeart } from "react-icons/fa";
import { describeArc } from "../utils/polar";
import { gaugeColor } from "../utils/gaugeColor";
import { CX, CY, ARC_END } from "./MainArc";

const R_FILL = 222;
const R_GUIDE = 233;
const GAUGE_TOP = 340;
const GAUGE_BOT = ARC_END; // 30°
const GAUGE_SPAN = 50; // 340° → 30° clockwise
const MARKER_START = 3;
const MARKER_END = 8;

const LABEL_X = 686;
const LABEL_Y = 105;
const ICON_X = 678;
const ICON_Y = 285;

// Static arc paths computed once
const GUIDE_PATH = describeArc(CX, CY, R_GUIDE, GAUGE_TOP, GAUGE_BOT);
const MARKER_PATH = describeArc(CX, CY, R_GUIDE, MARKER_START, MARKER_END);
const TRACK_BG_PATH = describeArc(CX, CY, R_FILL, GAUGE_TOP, GAUGE_BOT);

type Props = {
  health: number;
  maxHealth: number;
  onHealthMax?: () => void;
};

export const HealthGauge = memo(function HealthGauge({ health, maxHealth, onHealthMax }: Props) {
  const frac = Math.min(Math.max(health / maxHealth, 0), 1);
  const fillStartAngle = (GAUGE_BOT - frac * GAUGE_SPAN + 360) % 360;
  const color = gaugeColor(frac);

  useEffect(() => {
    if (health >= maxHealth) onHealthMax?.();
  }, [health, maxHealth, onHealthMax]);

  return (
    <>
      <defs>
        <filter id="redFillGlow" x="-80%" y="-40%" width="260%" height="180%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
      </defs>

      {/* Outer guide arc — 1px hairline */}
      <path
        d={GUIDE_PATH}
        fill="none"
        stroke="#D9D9D9"
        strokeWidth="1"
        strokeOpacity="0.15"
        strokeLinecap="butt"
      />
      {/* White marker — small 2px tick on the hairline */}
      <path
        d={MARKER_PATH}
        fill="none"
        stroke="#ffffff"
        strokeWidth="2"
        strokeOpacity="0.8"
        strokeLinecap="butt"
      />

      {/* Fill track background */}
      <path
        d={TRACK_BG_PATH}
        fill="none"
        stroke="#1a1c22"
        strokeWidth="7"
        strokeLinecap="butt"
      />
      {/* Red fill glow */}
      {frac > 0 && (
        <path
          d={describeArc(CX, CY, R_FILL, fillStartAngle, GAUGE_BOT)}
          fill="none"
          stroke={color}
          strokeWidth="7"
          strokeOpacity="0.4"
          strokeLinecap="butt"
          filter="url(#redFillGlow)"
        />
      )}
      {/* Red active fill */}
      {frac > 0 && (
        <path
          d={describeArc(CX, CY, R_FILL, fillStartAngle, GAUGE_BOT)}
          fill="none"
          stroke={color}
          strokeWidth="7"
          strokeLinecap="butt"
        />
      )}

      {/* Health number */}
      <text
        x={LABEL_X}
        y={LABEL_Y}
        textAnchor="start"
        dominantBaseline="hanging"
        fill="#ffffff"
        fontSize="25"
        fontWeight="600"
        className="purista-font"
      >
        {health}
      </text>
      {/* HEALTH label */}
      <text
        x={LABEL_X}
        y={LABEL_Y + 30}
        textAnchor="start"
        dominantBaseline="hanging"
        fill="#7a7d88"
        fontSize="12"
        className="purista-font uppercase"
        letterSpacing="0.22em"
      >
        HEALTH
      </text>

      {/* Heart icon */}
      <foreignObject x={ICON_X} y={ICON_Y} width={22} height={22}>
        <div style={{ color: "#ffffff", display: "flex" }}>
          <FaHeart size={22} />
        </div>
      </foreignObject>
    </>
  );
});
